import type { FC } from 'react'

import { visitorCameraPreviewContainer } from '@liveHelper/components/shared/VisitorCameraPreview/VisitorCameraPreview.twind'
import {
  cameraEnabledSelector,
  microphoneMutedSelector,
  useStore,
} from '@liveHelper/models/store'

import {
  AgoraTrackPlayer,
  LANDSCAPE_VIDEO_TRACK_CONFIG,
  useCamera,
  useMicrophone,
} from '@core/agoraRTC'
import { useBreakpoint } from '@core/components/Layout/Breakpoint/useBreakpoint'
import { AudioVisualizer } from '@core/components/twind/AudioVisualizer'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'
import { useScreenOrientation } from '@core/window/hooks'

import { getNameInitials } from '../../../../shared/utils/videoCall'

interface Props {
  visitorName: string
}

/**
 * This component is for visitor to test their camera and microphone
 */
export const VisitorCameraPreview: FC<Props> = ({ visitorName }) => {
  const { tw } = useTwindContext()

  const isCameraEnabled = useStore(cameraEnabledSelector)
  const isMicEnabled = !useStore(microphoneMutedSelector)
  const localVideoTrack = useCamera({
    allowed: isCameraEnabled,
    config: LANDSCAPE_VIDEO_TRACK_CONFIG,
  })
  const localAudioTrack = useMicrophone(isMicEnabled)

  const isMobileLayoutEnabled = isMobileWeb()
  const screenOrientation = useScreenOrientation()
  const isMobilePortrait =
    isMobileLayoutEnabled && screenOrientation === 'portrait'
  const { height } = useBreakpoint()

  return (
    <div
      className={tw?.(
        visitorCameraPreviewContainer(isMobilePortrait, height),
        'w-full',
        'overflow-hidden',
        'flex',
        'items-center',
        'justify-center',
        'rounded-xl',
      )}
    >
      {localVideoTrack ? (
        <AgoraTrackPlayer
          className={tw?.('w-full', 'h-full')}
          videoTrack={localVideoTrack}
        />
      ) : (
        <AudioVisualizer
          text={getNameInitials(visitorName)}
          audioTrack={localAudioTrack}
        />
      )}
    </div>
  )
}
